import { Injectable } from '@angular/core';
import { IdleRobotEscalation, IdleRobotEscalationCount } from '@ng-cloud/badger-core/models/idle-robot-escalation';
import { RobotEscalation } from '@ng-cloud/badger-core/models/robot-escalation';
import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ApiService } from './api.service';

@Injectable()
export class RobotEscalationService extends ApiService {

  /** Fallback for servers that predate the idle_minutes field on the idle_count response. */
  private static readonly DEFAULT_IDLE_MINUTES = 20;

  /**
   * Fetch all RobotEscalations
   */
  listRobotEscalations(params?: any): Observable<any[]> {
    return this.list(RobotEscalation, 'robot_escalations', params);
  }

  /**
   * Fetch RobotEscalation data for the given id
   */
  getRobotEscalation(id: number): Observable<any> {
    return this.get(RobotEscalation, `robot_escalations/${id}`, 300);
  }

  /**
   * Fetch idle RobotEscalation counts for the header badge.
   *
   * Deliberately bypasses the request cache: the badge polls on a fixed interval and must never be
   * answered from a cached response older than that interval.
   *
   * @param lastSeenAt ISO8601 timestamp of the last time the user opened the idle dialog. Omitted
   *   entirely when unknown, which the server treats as "everything is new".
   */
  getIdleRobotEscalationCount(lastSeenAt?: string): Observable<IdleRobotEscalationCount> {
    const params = ApiService.params(lastSeenAt ? { last_seen_at: lastSeenAt } : {});

    return this.http.get<any>(this.url('robot_escalations/idle_count'), { params }).pipe(
      map(response => ({
        count: response?.count ?? 0,
        newCount: response?.new_count ?? 0,
        idleMinutes: response?.idle_minutes ?? RobotEscalationService.DEFAULT_IDLE_MINUTES
      })),
      catchError(e => this.handleError(e)));
  }

  /**
   * Fetch the slim list of idle RobotEscalations shown in the idle dialog.
   *
   * Passing a cache duration of 0 keeps in-flight de-duplication while ensuring a subsequent fetch
   * (e.g. after a snooze or reassign) always hits the server.
   */
  listIdleRobotEscalations(params?: any): Observable<IdleRobotEscalation[]> {
    return this.list(IdleRobotEscalation, 'robot_escalations/idle', params, 0);
  }

  /**
   * Update RobotEscalation
   */
  updateRobotEscalation(robotEscalation: RobotEscalation | IdleRobotEscalation): Observable<any> {
    return this.update(robotEscalation, `robot_escalations/${robotEscalation.id}`);
  }

  /**
   * Snooze RobotEscalation with a minimal PATCH payload
   */
  snoozeRobotEscalation(id: number, snoozeMinutes: number, assignedTo: number | null): Observable<any> {
    const payload = {
      status: 'snoozed',
      snooze_time_min: snoozeMinutes,
      assigned_to: assignedTo ?? null
    };

    return this.http.patch(this.url(`robot_escalations/${id}`), payload).pipe(
      tap(() => this.cachedRequests.clear())
    );
  }

}
